import { EzBadParamException } from '/ezlibrary/exceptions/EzBadParamException.js';

import { EzObject } from '/ezlibrary/helpers/EzObject.js';
import { EzBoolean } from '/ezlibrary/helpers/EzBoolean.js';
import { EzString } from '/ezlibrary/helpers/EzString.js';
import { EzArray } from '/ezlibrary/helpers/EzArray.js';
import { EzFunction } from '/ezlibrary/helpers/EzFunction.js';
import { EzHtml } from '/ezlibrary/helpers/EzHtml.js';

import { EzUx } from '/ezlibrary/ux/EzUx.js';

import { EzDialogConfig } from '/ezlibrary/ux/dialogs/EzDialogConfig.js';


/**
 * @class
 * @description
 * A basic UX dialog that allows for an optional custom icon on the top left
 * and custom HTML content.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzClockerDialog } from '/ezlibrary/ux/dialogs/EzClockerDialog.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzClockerDialog {
    /**
     * @static
     * @public @readonly @property
     * Gets the Id of the hidden dialogs container
     * (for the DIV container inject by globalThis.ezApi.ezclocker.ezDialog.ezInjectHiddenDialogContainer())
     *
     * @returns {string}
     */
    static get DEFAULT_HIDDEN_DIALOG_CONTAINER_ID() {
        return 'EzHiddenDialogsContainer';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the dialog's hidden dialog container (aka parent container) HTML
     *
     * Using CSS class information:
     *  1) ezDisplay-hidden: Defined in ez-body.css
     *
     * @returns {string}
     */
    static get hiddenDialogContainerHTML() {
        return EzHtml.build`
            <div
                id="${EzClockerDialog.DEFAULT_HIDDEN_DIALOG_CONTAINER_ID}"
                class="ezDisplay-hidden">
            </div>`;
    }

    /**
     * @public @method
     * Creates the initial EzDialogConfig instance with the provided dialogId.
     *
     * @param {string} dialogId
     *
     * @returns {EzDialogConfig}
     */
    static ezCreateDialogConfig(dialogId) {
        if (!EzString.stringHasLength(dialogId)) {
            throw new EzBadParamException(
                'dialogId',
                EzClockerDialog,
                `static ${EzClockerDialog.ezCreateDialogConfig.toString()}`);
        }

        return new EzDialogConfig(dialogId);
    }

    /**
     * @public @constructor
     * Creates a new instance of EzClockerDialog.
     *
     * @param {EzDialogConfig} ezDialogConfig
     * Default: new EzDialogConfig()
     */
    constructor(ezDialogConfig = null) {
        if (!EzObject.isValid(ezDialogConfig)) {
            ezDialogConfig = new EzDialogConfig();
        } else if ('EzDialogConfig' !== ezDialogConfig.constructor.name) {
            throw new EzBadParamException(
                'ezDialogConfig of type EzDialogConfig',
                this,
                this.constructor);
        }

        this.#ezDialogConfig = ezDialogConfig;
    }

    /**
     * @public @readonly @property
     * Gets a map of id = HTML element id for commonly referenced HTML elements.
     *
     * @returns {object}
     */
    get ezIds() {
        return {
            containers: {
                hiddenDialogContainerId: EzClockerDialog.DEFAULT_HIDDEN_DIALOG_CONTAINER_ID,
                dialogContainerId: `${this.ezDialogConfig.ezDialogId}_DialogContainer`,
                dialogContentContainerId: `${this.ezDialogConfig.ezDialogId}_DialogContentContainer`,
                dialogIconContainerId: `${this.ezDialogConfig.ezDialogId}_IconContainer`
            },
            images: {
                dialogIconImageId: `${this.ezDialogConfig.ezDialogId}_Icon`
            }
        };
    }

    /**
     * @public @readonly @property
     * Gets the jQuery reference to the element associated with id=this.ezDialogConfig.ezDialogId
     *
     * @returns {object}
     * The jQuery reference to the element associated with id=this.ezDialogConfig.ezDialogId
     */
    get $dialogElement() {
        const jQueryDialogElement = EzUx.id$(this.ezDialogConfig.ezDialogId);

        if (!EzObject.isValid(jQueryDialogElement) || 0 === jQueryDialogElement.length) {
            throw new EzException(
                `Unable to obtain the jQuery element reference for the dialog with id=${this.ezDialogConfig.ezDialogId}.`);
        }

        return jQueryDialogElement;
    }

    /**
     * @public @property @getter
     * Gets the dialog's HTML content
     *
     * @returns {string}
     */
    get dialogContent() {
        return this.ezDialogConfig.ezDialogContentHtml;
    }
    /**
     * @public @method
     * Replaces the dialog's content with the provided dialogContent value.
     *
     * @param {string} dialogContent
     */
    set dialogContent(dialogContent = EzDialogConfig.DEFAULT_DIALOG_CONTENT) {
        this.ezDialogConfig.ezDialogContentHtml = EzString.isString(dialogContent)
            ? dialogContent
            : EzDialogConfig.DEFAULT_DIALOG_CONTENT;

        EzUx.setContent(
            this.ezIds.containers.dialogContentContainerId,
            this.ezDialogConfig.ezDialogContentHtml);
    }

    /**
     * @public @readonly @property
     * Gets if the dialog is showing or not
     *
     * @type {boolean}
     */
    get ezVisible() {
        return EzBoolean.isTrue(
            EzUx.elementExists(this.ezDialogConfig.ezDialogId) && this.$dialogElement?.dialog('isOpen'));
    }

    /**
     * @public @readonly @property
     * Gets if the dialog's html is injected into the document body.
     *
     * @type {boolean}
     */
    get ezInjected() {
        return EzBoolean.isTrue(
            EzUx.elementExists(this.ezDialogConfig.ezDialogId));
    }

    /**
     * @private @field
        Stores the EzDialogConfig instance for the dialog.
     * @type {EzDialogConfig}
     */
    #ezDialogConfig = null;
    /**
     * @public @readonly @property
     * Gets the EzDialogConfig instance for the dialog.
     * @returns {EzDialogConfig}
     */
    get ezDialogConfig() {
        return this.#ezDialogConfig;
    }
    /**
     * @public @proeprty @setter
     * Sets the EzDialogConfig instance for the dialog.
     * If the dialog is already injected or showing then it is closed, and removed from the document
     * before the configuration is updated.
     * @returns {EzDialogConfig}
     */
    set ezDialogConfig(ezDialogConfig) {
        globalThis.ezApi.ezclocker.ezEventEngine.ezUnwantEvent(
            this.#ezDialogConfig.ezEventNames.onEzDialogConfigUpdated,
            this.ezDialogId);

        this.#ezDialogConfig = ezDialogConfig;

        globalThis.ezApi.ezclocker.ezEventEngine.ezWantEvent(
            this.#ezDialogConfig.ezEventNames.onEzDialogConfigUpdated,
            this.ezDialogId,
            (ezEvent) => this.ezHandleEzDialogConfigUpdatedEvent(ezEvent).apply(this));
    }

    /**
     * @public @method
     * Builds the dialogs HTML
     *
     * @returns {string}
     */
    get dialogHTML() {
        return EzHtml.build`
            <div
                id="${this.ezDialogConfig.ezDialogId}"
                title="${this.ezDialogConfig.title}">
                ${this.dialogContentHTML}
            </div>`;
    }

    /**
     * @public @readonly @property
     * Builds the dialog's Icon display HTML if the ezDialogConfig.ezDialogIconUrl is provided.
     *
     * Used CSS class information:
     *  1) ezDialog-icon-container: Defined in ez-dialog.css
     *  2) ezDialog-icon: Defined in ez-dialog.css
     *
     * @returns {string}
     */
    get dialogIconHTML() {
        return EzString.hasLength(this.ezDialogConfig.ezDialogIconUrl)
            ? EzHtml.build`
                <div
                    id="${this.ezIds.containers.dialogIconContainerId}"
                    class="ezDialog-icon-container">
                    <img
                        id="${this.ezIds.images.dialogIconImageId}"
                        src="${this.ezDialogConfig.ezDialogIconUrl}"
                        style="ezDialog-icon"/>
                </div>`
            : EzString.EMPTY;
    }

    /**
     * @public @readonly @property
     * Builds the dialogs content container an inserts the optional icon html and ezDialogConfig.ezDialogContentHtml.
     *
     * Used CSS class information:
     *  1) ezAutoCol_MinxA: Defined in ez-layout-grid.css
     *  2) ezAutoCol_A: Defined in ez-layout-grid.css
     *  3) ezGrid-align-full: Defined in ez-layout-grid.css
     *
     * @returns {string}
     */
    get dialogContentHTML() {
        if (!EzString.isString(this.ezDialogConfig.ezDialogContentHtml)) {
            // Creating default dialog content
            this.ezDialogConfig.ezDialogContentHtml = EzDialogConfig.DEFAULT_DIALOG_CONTENT;
        }

        const dialogContentClass = EzString.hasLength(this.ezDialogConfig.ezDialogIconUrl)
            ? 'ezAutoCol_MinxA'
            : 'ezAutoCol_A';

        return EzHtml.build`
            <div
                id="${this.ezIds.containers.dialogContainerId}"
                class="${dialogContentClass}">
                ${this.dialogIconHTML}
                <div
                    id="${this.ezIds.containers.dialogContentContainerId}"
                    class="ezGrid-align-full">
                    ${this.ezDialogConfig.ezDialogContentHtml}
                </div>`;
    }

    /**
     * @public @method
     * Shows the dialog to the user
     *
     * @param {EzDialogConfig} ezDialogConfig
     */
    ezShow(ezDialogConfig) {
        if (EzObject.isValid(ezDialogConfig)) {
            this.ezDialogConfig = ezDialogConfig;
        }

        if (EzBoolean.isFalse(this.ezInjected)) {
            this.ezInjectHiddenDialogContainer();

            EzUx.appendContent(
                EzClockerDialog.DEFAULT_HIDDEN_DIALOG_CONTAINER_ID,
                this.dialogHTML);

            this.$dialogElement?.dialog(
                this.ezDialogConfig.asJQueryUIDialogConfig);

            if (EzFunction.isFunction(this.ezDialogConfig.ezCloseButtonHandler)) {
                this.ezSetDialogTitleCloseButtonClickHandler(this.ezDialogConfig.ezCloseButtonHandler);
            }
        }

        this.$dialogElement?.dialog('open');
    }

    /**
     * @public @method
     * Injects the dialog's hidden dialog parent container if it does not exist.
     */
    ezInjectHiddenDialogContainer() {
        if (!EzUx.elementExists(EzClockerDialog.DEFAULT_HIDDEN_DIALOG_CONTAINER_ID)) {
            EzUx.appendContent(
                EzUx.bodyTagName,
                EzClockerDialog.hiddenDialogContainerHTML);
        }
    }

    /**
     * @public @method
     * Sets a Jquery dialog's title bar close button click event.
     * The button is located by the aria-describedby attribute and is assumed to
     * equal the dialogId it applies to.
     *
     * @param {function} onClickHandler
     */
    ezSetDialogTitleCloseButtonClickHandler(onClickHandler) {
        if (!EzFunction.isFunction(onClickHandler)) {
            throw new EzBadParamException(
                'onClickHandler',
                this,
                this.ezSetDialogTitleCloseButtonClickHandler);
        }
        if (!EzUx.elementExists(this.ezDialogConfig.ezDialogId)) {
            throw new EzException(`Unable to locate the dialog HTML element with id=${this.ezDialogConfig.ezDialogId}.`);
        }

        // Hook the dialog's small close button click event (in the title)
        const elements = EzUx.findByAriaAttributeSuffix(
            'describedby',
            this.ezDialogConfig.ezDialogId);

        if (!EzArray.hasLength(elements)) {
            return;
        }

        // Set the click event handler for the elements
        for (let element of elements) {
            element.click = onClickHandler;
        }
    }

    /**
     * @public @method
     * Closes the dialog when visible to the user
     *
     * @param {boolean} remove
     * Default: true
     */
    ezClose(remove = true) {
        if (EzBoolean.isTrue(this.ezVisible)) {
            this.$dialogElement?.dialog('close');
        }

        if (EzBoolean.booleanOrTrue(remove) && EzUx.elementExists(this.ezDialogConfig.ezDialogId)) {
            this.ezDestroy();
        }
    }

    /**
     * @public @method
     * Closes the dialog then destroys and removes the dialog from the document.
     */
    ezDestroy() {
        if (EzUx.elementExists(this.ezDialogConfig.ezDialogId)) {
            const jQueryDialogElement = this.$dialogElement;

            jQueryDialogElement?.dialog('close');

            jQueryDialogElement?.dialog('destroy')

            jQueryDialogElement?.remove();

            EzUx.removeElement(this.ezDialogConfig.ezDialogId);
        }

        const hiddenDialogContainerId = `${this.ezDialogConfig.ezDialogId}_HiddenDialogContainer`;

        if (EzUx.elementExists(hiddenDialogContainerId) && !EzString.hasLength(EzUx.getContent(hiddenDialogContainerId))) {
            // Remove the injected hidden container if empty
            EzUx.removeElement(hiddenDialogContainerId);
        }
    }

    /**
     * @protected @method
     * Handles the ezDialogConfig.ezEventNames.onEzDialogConfigUpdated event
     *
     * @param {ezEvent}
     * ezEvent.data = {
     *     dialogId: this.ezDialogId,
     *     updatedProperties: {
     *         {propName1}: {propValue1},
     *         {propName2}: {propValue2},
     *             ...
     *         {propNameN}: {propValueN},
     *     ]
     *     ezDialogConfig: this
     * }
     */
    ezHandleEzDialogConfigUpdatedEvent(ezEvent) {
        if (EzObject.isValid(ezEvent) && EzObject.isValid(ezEvent.data)) {
            // Update the dialog's configuration
            this.$dialogElement?.dialog(ezEvent.data.ezDialogConfig.asJQueryUIDialogConfig);
        }
    }
}
